import React, { useEffect, useRef, useState } from 'react';
import { contactApiService, type ProductLine } from '@metlas/contact';
import './LandingPageV2.css';
import columbiaLogo from './assets/columbia-logo.png';
import stanfordLogo from './assets/stanford-logo.svg';
import shotUnderwritingModel from './assets/screens/underwriting-model.webp';
import shotSiteSourcing from './assets/screens/site-sourcing.webp';
import shotDevelopment from './assets/screens/development-management.webp';
import shotPropertyMgmt from './assets/screens/property-management.webp';
import shotSmartBrokerage from './assets/screens/smart-brokerage.webp';
import shotFinance from './assets/screens/finance.webp';
import logoCbcGlobe from './assets/logos/cbc-globe.png';
import logoSj from './assets/logos/sj-international.png';
import logoZion from './assets/logos/zion-enterprises.png';
import logoMicrosoft from './assets/logos/microsoft.svg';
import logoMeta from './assets/logos/meta.svg';
import logoBarracuda from './assets/logos/barracuda.svg';
import logoEngineeringCapital from './assets/logos/engineering-capital.png';
import logoAminoCapital from './assets/logos/amino-capital.png';
import logoAnorisCapital from './assets/logos/anoris-capital.png';

/* ── Links (mirrors frontend/src/constants/routes.ts + /REdevelopers) ── */
const LINKS = {
  leaseManagement: 'https://metlas.ai/apps/lease_management',
  buildingIntelligence: 'https://metlas.ai/apps/building-intelligence',
  developmentManagement: 'https://metlas.ai/apps/development_management#/',
  aiCrm: 'https://metlas.ai/apps/ai_crm/',
  /** Free Sawbridge tools, hosted on sawbridgeai.com. */
  qualifiedLeads: 'https://www.sawbridgeai.com/qualified-leads',
  documentScreening: 'https://www.sawbridgeai.com/document-screening',
  proformaAnalyst: 'https://www.sawbridgeai.com/proforma-assistant',
  privacyTerms: '/privacy-terms',
  careers: '/careers',
  contact: '/contact',
} as const;

/* ── Copy ─────────────────────────────────────────────────────── */
type NavItem = { href: string; label: string; agent?: string };
type NavEntry = { label: string; href: string; items?: NavItem[]; badge?: string };

const NAV: NavEntry[] = [
  {
    label: 'Development management',
    href: '#knightsbridge',
    items: [
      { href: '#kb-source', label: 'Source and prospect', agent: 'Parcel agent' },
      { href: '#kb-underwrite', label: 'Underwrite', agent: 'Underwriting agent' },
      { href: '#kb-build', label: 'Manage the build', agent: 'Development agent' },
    ],
  },
  {
    label: 'Asset management',
    href: '#sawbridge',
    items: [
      { href: '#sb-leads', label: 'Smart brokerage', agent: 'Sales agent' },
      { href: '#sb-operate', label: 'Operate', agent: 'Rental system' },
      { href: '#sb-finance', label: 'Finance', agent: 'Finance copilot' },
    ],
  },
  { label: 'Why Metlas', href: '#why' },
  { label: 'Customers', href: '#customers' },
  { label: 'Careers', href: LINKS.careers, badge: 'We’re hiring' },
];

/** Smooth-scroll to an in-page anchor; scroll-margin-top on [id] keeps it clear of the fixed nav. */
function goTo(e: React.MouseEvent<HTMLAnchorElement>, href: string) {
  const el = document.querySelector<HTMLElement>(href);
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.history.replaceState(null, '', href);
}

type Step = {
  id: string;
  verb: string;
  title: string;
  copy: string;
  shot: { src: string; alt: string; position?: string };
  links?: { href: string; label: string }[];
};

const KNIGHTSBRIDGE: Step[] = [
  {
    id: 'kb-source',
    verb: 'Source and prospect',
    title: 'Find the lot before the market does.',
    copy: 'Filter lots by your criteria, then prospect the owners with a follow-up strategy tailored to each profile.',
    shot: {
      src: shotSiteSourcing,
      alt: 'Parcel dossier with zoning, floor area, and owner signals',
    },
    links: [{ href: LINKS.buildingIntelligence, label: 'Investigate a parcel' }],
  },
  {
    id: 'kb-underwrite',
    verb: 'Underwrite',
    title: 'From OM to decision in minutes.',
    copy: 'Cash flow, returns, and the claims that don’t check out, then an LP-ready memo.',
    shot: {
      src: shotUnderwritingModel,
      alt: 'Underwriting model with live assumptions, returns, and verdict',
    },
    links: [{ href: '#waitlist', label: 'Run the underwriting agent' }],
  },
  {
    id: 'kb-build',
    verb: 'Manage the build',
    title: 'Every milestone on one timeline.',
    copy: 'Contractor budgets, agency correspondence, and filings in one place, with what’s due flagged before it’s late.',
    shot: {
      src: shotDevelopment,
      alt: 'Development progress tree with parties, items, and the meeting timeline',
    },
    links: [{ href: LINKS.developmentManagement, label: 'Run a development team' }],
  },
];

const SAWBRIDGE: Step[] = [
  {
    id: 'sb-leads',
    verb: 'Smart brokerage',
    title: 'Every inquiry answered. Every tour booked.',
    copy: 'Leads reach out by text or email; the agent answers on their channel, qualifies them, and books the showing.',
    shot: { src: shotSmartBrokerage, alt: 'Leasing inbox with the agent answering a prospect by text' },
    links: [
      { href: LINKS.aiCrm, label: 'Run the CRM' },
      { href: LINKS.qualifiedLeads, label: 'Try the lead generation tool for free' },
    ],
  },
  {
    id: 'sb-operate',
    verb: 'Operate',
    title: 'Rent, repairs, and renewals, handled.',
    copy: 'Tenants self-serve in the app. Live comps and concessions for your building, exported to Excel. You get one line when a call is yours.',
    shot: { src: shotPropertyMgmt, alt: 'Property management operations console' },
    links: [
      { href: LINKS.leaseManagement, label: 'Run the rental system' },
      { href: LINKS.documentScreening, label: 'Try the fraudulent document screening tool for free' },
    ],
  },
  {
    id: 'sb-finance',
    verb: 'Finance',
    title: 'Clean books. A sharper next deal.',
    copy: 'The copilot codes and reconciles every transaction, watches your loans for refinancing windows, and feeds actual results back into your investment model.',
    shot: { src: shotFinance, alt: 'Finance copilot reconciling the ledger and flagging a refinancing window' },
    links: [{ href: '#waitlist', label: 'Run the finance copilot' }],
  },
];

const PILLARS = [
  {
    title: 'Grounded in the record',
    copy: 'Answers come from the up-to-date public record and private data, and every one shows its source.',
  },
  {
    title: 'Purpose-built with practicing developers',
    copy: 'Developers operating in California and the Northeast shaped how the agents work.',
  },
  {
    title: 'Verified before it runs',
    copy: 'Backed by a decade of trustworthy-AI research. Every action is checked first, and when an agent isn’t sure, it asks.',
  },
];

const BACKERS = [
  { name: 'Engineering Capital', href: 'https://www.engineeringcapital.com/', logo: logoEngineeringCapital, height: 30 },
  { name: 'Amino Capital', href: 'https://www.aminocapital.com/', logo: logoAminoCapital, height: 26 },
  { name: 'Anoris Capital', href: 'https://anoriscapital.com/', logo: logoAnorisCapital, height: 26 },
];

/** Free tools anyone can open today. */
const FREE_TOOLS: { name: string; copy: string; note?: string; href: string }[] = [
  {
    name: 'Fraudulent Document Screening',
    copy: 'Bank statements, pay stubs, and tax returns checked for tampering.',
    href: LINKS.documentScreening,
  },
  {
    name: 'Qualified Leads Generation',
    copy: 'AI listing campaigns on every social channel, with every lead in one inbox.',
    href: LINKS.qualifiedLeads,
  },
  {
    name: 'Proforma Analyst',
    copy: 'Upload a proforma or just an address. Get breakeven, margins, and max land price.',
    href: LINKS.proformaAnalyst,
  },
  {
    name: 'Rental Management',
    copy: 'Leases, rent, repairs, and renewals, with a portal for every tenant.',
    note: 'Invite only',
    href: LINKS.leaseManagement,
  },
];

type Quote = { product: string; text: string; who: string };

const QUOTES: Quote[] = [
  {
    product: 'Underwriting',
    text: 'Without an analyst on staff, every OM meant a night in Excel before we could even say maybe. Now we drop the OM in and have the cash flow, returns and a list of the claims that don’t check out within minutes.',
    who: 'Managing Partner, boutique investment firm',
  },
  {
    product: 'Property management',
    text: 'Leases, rent, repairs and renewals used to live in five different places. Now our tenants handle most of it themselves in the app, and the to-dos and reminders it manages for us mean nothing slips through the cracks.',
    who: 'Property Manager, residential rental company',
  },
  {
    product: 'Lead acquisition',
    text: 'Every listing inquiry used to wait until an agent was free. Now it’s answered in seconds, the buyer or tenant is qualified, and the showing is booked. We stopped losing leads to whoever replied first.',
    who: 'Managing Broker, commercial brokerage',
  },
  {
    product: 'Development management',
    text: 'We run four projects with a team of three. Every contractor budget, agency correspondence, and filing sits on one timeline and the agent flags what’s due, so nothing surprises us at the Monday meeting.',
    who: 'Development Director, international developer',
  },
  {
    product: 'Site sourcing',
    text: 'We used to pull BIS, DOB, ACRIS, and zoning by hand for every lot we liked. Now we ask the dossier what we can build as-of-right and have the answer, with the filings, before we walk the block.',
    who: 'Principal, development studio',
  },
];

/** Hero background footage: served from frontend/public/videos (Mixkit free licence, self-hosted). */
const HERO_CLIPS = [
  '/videos/hero_desk.mp4',
  '/videos/hero_site.mp4',
  '/videos/hero_model.mp4',
  '/videos/hero_closing.mp4',
  '/videos/hero_office.mp4',
];

const ROLES = ['GP / developer', 'Owner-operator', 'Property manager', 'Investor', 'Broker', 'Other'];
const PORTFOLIO = ['Under 50 units', '50–500 units', '500–5,000 units', '5,000+ units', 'Not applicable'];

/* ── Pieces ───────────────────────────────────────────────────── */
export const Logo: React.FC = () => (
  <span className="mlp-logo">
    <svg className="mlp-logo-mark" viewBox="0 0 64 56" aria-hidden="true">
      <polygon points="0,0 26,13 26,56 0,56" fill="#16213a" />
      <polygon points="33,20 62,3 62,56 42,56" fill="#4a90d9" />
    </svg>
    <span className="mlp-logo-word">Metlas</span>
  </span>
);

/** Customer marks. Two are image logos; three are wordmarks set to match each company's own site. */
const CustomerLogos: React.FC = () => (
  <>
    <span className="mlp-mark mlp-mark--lmt" title="LMT Home Corporation">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 11.5 12 4l9 7.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.5 10v9.5h13V10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
      <span>
        LMT Home
        <br />
        Corporation
      </span>
    </span>
    <span className="mlp-mark mlp-mark--cbc" title="Commercial Brokers of California">
      <img src={logoCbcGlobe} alt="" />
      <span>Commercial Brokers of California</span>
    </span>
    <span className="mlp-mark mlp-mark--sj" title="SJ International Development">
      <img src={logoSj} alt="SJ International Development" />
    </span>
    <span className="mlp-mark mlp-mark--zion" title="Zion Enterprises">
      <img src={logoZion} alt="Zion Enterprises" />
    </span>
    <span className="mlp-mark mlp-mark--studio" title="Studio Analogies">
      <span>Studio Analogies</span>
    </span>
    <span className="mlp-mark mlp-mark--columbia" title="Columbia University">
      <img src={columbiaLogo} alt="Columbia University" />
    </span>
    <span className="mlp-mark mlp-mark--fieldwork" title="Fieldwork">
      <svg viewBox="0 0 24 30" aria-hidden="true">
        <rect x="2" y="2" width="1.5" height="26" />
        <rect x="2" y="2" width="16" height="1.5" />
        <rect x="2" y="13" width="12" height="1.5" />
      </svg>
      <span>fieldwork</span>
    </span>
  </>
);

const Shot: React.FC<{ src: string; alt: string; position?: string; className?: string }> = ({
  src,
  alt,
  position,
  className,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  return (
    <figure className={`mlp-shot${className ? ` ${className}` : ''}`}>
      <span className="mlp-shot-bar" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <button
        type="button"
        className="mlp-shot-open"
        onClick={() => dialogRef.current?.showModal()}
        aria-label={`View full screen: ${alt}`}
      >
        <img src={src} alt={alt} style={position ? { objectPosition: position } : undefined} loading="lazy" />
      </button>
      {/* Native modal: Esc, a click anywhere, or the close button dismisses it. */}
      <dialog ref={dialogRef} className="mlp-lightbox" aria-label={alt} onClick={() => dialogRef.current?.close()}>
        <img src={src} alt={alt} />
        <button type="button" className="mlp-lightbox-close" aria-label="Close">
          ×
        </button>
      </dialog>
    </figure>
  );
};

const StepRow: React.FC<{ step: Step; index: number; tone: 'kb' | 'sb' }> = ({ step, index, tone }) => (
  <div id={step.id} className={`mlp-step${index % 2 ? ' mlp-step--flip' : ''}`} data-reveal>
    <div className="mlp-step-text">
      <p className={`mlp-step-verb mlp-step-verb--${tone}`}>
        <span>{String(index + 1).padStart(2, '0')}</span> {step.verb}
      </p>
      <h3 className="mlp-step-title">{step.title}</h3>
      <p className="mlp-step-copy">{step.copy}</p>
      {step.links && (
        <div className="mlp-step-links">
          {step.links.map((l) => (
            <a
              key={l.href}
              className={`mlp-textlink mlp-textlink--${tone}`}
              href={l.href}
              onClick={(e) => goTo(e, l.href)}
            >
              {l.label} <span aria-hidden="true">→</span>
            </a>
          ))}
        </div>
      )}
    </div>
    <Shot {...step.shot} />
  </div>
);

/** One row of quotes that rolls one card every few seconds. The list is rendered twice so the
 *  row stays full while it rolls; on reaching the copy it snaps back to the start without animating.
 *  Pauses on hover or focus, and never auto-rolls for users who prefer reduced motion. */
const QuoteCarousel: React.FC<{ quotes: Quote[] }> = ({ quotes }) => {
  const n = quotes.length;
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      setAnimate(true);
      setIndex((i) => Math.min(i + 1, n));
    }, 5000);
    return () => window.clearInterval(id);
  }, [paused, n]);

  // Turn animation back on the frame after a silent jump.
  useEffect(() => {
    if (animate) return;
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
    return () => cancelAnimationFrame(id);
  }, [animate]);

  // Once the roll lands on the copy, snap back to the matching original after the slide finishes.
  // A timer rather than transitionend, which never fires while the tab is in the background.
  useEffect(() => {
    if (index < n) return;
    const id = window.setTimeout(() => {
      setAnimate(false);
      setIndex((i) => i - n);
    }, 750);
    return () => window.clearTimeout(id);
  }, [index, n]);

  const next = () => {
    setAnimate(true);
    setIndex((i) => Math.min(i + 1, n));
  };
  const prev = () => {
    if (index > 0) {
      setAnimate(true);
      setIndex(index - 1);
      return;
    }
    // Jump silently to the copy, then roll back one.
    setAnimate(false);
    setIndex(n);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setAnimate(true);
        setIndex(n - 1);
      })
    );
  };

  return (
    <div
      className="mlp-quotes"
      aria-roledescription="carousel"
      aria-label="Customer quotes"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="mlp-quotes-viewport">
        <div
          className={`mlp-quotes-track${animate ? '' : ' is-jumping'}`}
          style={{ '--mlp-quote-i': index } as React.CSSProperties}
        >
          {[...quotes, ...quotes].map((q, i) => (
            <figure key={`${q.product}-${i}`} className="mlp-quote" aria-hidden={i >= n || undefined}>
              <span className="mlp-quote-product">{q.product}</span>
              <blockquote>“{q.text}”</blockquote>
              <figcaption>{q.who}</figcaption>
            </figure>
          ))}
        </div>
      </div>
      <div className="mlp-quotes-controls">
        <button type="button" className="mlp-quotes-btn" aria-label="Previous quote" onClick={prev}>
          <span aria-hidden="true">←</span>
        </button>
        <button type="button" className="mlp-quotes-btn" aria-label="Next quote" onClick={next}>
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  );
};

/* ── Waitlist form ─────────────────────────────────────────────── */
const WaitlistForm: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [portfolioSize, setPortfolioSize] = useState('');
  const [lines, setLines] = useState<ProductLine[]>([]);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const toggleLine = (line: ProductLine) =>
    setLines((prev) => (prev.includes(line) ? prev.filter((l) => l !== line) : [...prev, line]));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setError('');
    try {
      const res = await contactApiService.submit({
        name,
        email,
        company: company || undefined,
        role: role || undefined,
        portfolioSize: portfolioSize || undefined,
        productLines: lines.length ? lines : undefined,
        source: 'waitlist',
      });
      if (res.error) {
        setStatus('error');
        setError(res.error);
        return;
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="mlp-form mlp-form--done" role="status">
        <h3>You’re on the list.</h3>
        <p>We onboard in order and will be in touch at {email}.</p>
      </div>
    );
  }

  const busy = status === 'submitting';
  return (
    <form className="mlp-form" onSubmit={onSubmit}>
      <div className="mlp-form-grid">
        <label>
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required disabled={busy} autoComplete="name" />
        </label>
        <label>
          <span>Work email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={busy} autoComplete="email" />
        </label>
        <label>
          <span>Company</span>
          <input value={company} onChange={(e) => setCompany(e.target.value)} disabled={busy} autoComplete="organization" />
        </label>
        <label>
          <span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
            <option value="">Select</option>
            {ROLES.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>
        <label className="mlp-form-wide">
          <span>Portfolio size</span>
          <select value={portfolioSize} onChange={(e) => setPortfolioSize(e.target.value)} disabled={busy}>
            <option value="">Select</option>
            {PORTFOLIO.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </label>
      </div>
      <fieldset className="mlp-form-lines">
        <legend>Interested in</legend>
        <label>
          <input type="checkbox" checked={lines.includes('knightsbridge')} onChange={() => toggleLine('knightsbridge')} disabled={busy} />
          Knightsbridge <small>development management</small>
        </label>
        <label>
          <input type="checkbox" checked={lines.includes('sawbridge')} onChange={() => toggleLine('sawbridge')} disabled={busy} />
          Sawbridge <small>asset management</small>
        </label>
      </fieldset>
      {status === 'error' && <p className="mlp-form-error">{error}</p>}
      <button type="submit" className="mlp-btn mlp-btn--primary" disabled={busy}>
        {busy ? 'Sending…' : 'Join the waitlist'}
      </button>
    </form>
  );
};

/** The site header. On the landing page in-page links scroll; on subpages (base="/")
 *  they become links back to the matching landing section. */
export const SiteHeader: React.FC<{ base?: string }> = ({ base = '' }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const link = (href: string) => (href.startsWith('#') ? `${base}${href}` : href);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close an open dropdown on outside click or Escape (hover handles the mouse case).
  useEffect(() => {
    if (!openGroup) return;
    const onDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.mlp-nav-group')) setOpenGroup(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenGroup(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openGroup]);

  const closeMenu = () => {
    setMenuOpen(false);
    setOpenGroup(null);
  };

  return (
    <header className={`mlp-nav${scrolled ? ' is-scrolled' : ''}${menuOpen ? ' is-open' : ''}`}>
      <div className="mlp-wrap mlp-nav-inner">
        <a href={base ? base : '#top'} className="mlp-nav-brand" aria-label="Metlas home">
          <Logo />
        </a>
        <nav className="mlp-nav-links" aria-label="Primary">
          {NAV.map((entry) =>
            entry.items ? (
              <div
                key={entry.href}
                className={`mlp-nav-group${openGroup === entry.label ? ' is-open' : ''}`}
                onMouseEnter={() => setOpenGroup(entry.label)}
                onMouseLeave={() => setOpenGroup((g) => (g === entry.label ? null : g))}
              >
                <button
                  type="button"
                  className="mlp-nav-group-btn"
                  aria-expanded={openGroup === entry.label}
                  aria-haspopup="true"
                  onClick={() => setOpenGroup(entry.label)}
                >
                  {entry.label}
                  <svg viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div className="mlp-nav-menu" role="menu">
                  {entry.items.map((it) => (
                    <a
                      key={it.href}
                      href={link(it.href)}
                      role="menuitem"
                      onClick={(e) => {
                        goTo(e, link(it.href));
                        closeMenu();
                      }}
                    >
                      <span>{it.label}</span>
                      {it.agent && <small>{it.agent}</small>}
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <a
                key={entry.href}
                href={link(entry.href)}
                onClick={(e) => {
                  goTo(e, link(entry.href));
                  closeMenu();
                }}
              >
                {entry.label}
                {entry.badge && <span className="mlp-nav-badge">{entry.badge}</span>}
              </a>
            )
          )}
        </nav>
        <div className="mlp-nav-actions">
          <a href={link('#waitlist')} className="mlp-btn mlp-btn--primary mlp-btn--small" onClick={closeMenu}>
            Join the waitlist
          </a>
          <button
            type="button"
            className="mlp-nav-burger"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
    </header>
  );
};

/* ── Page ─────────────────────────────────────────────────────── */
const LandingPageV2: React.FC = () => {
  const [clip, setClip] = useState(0);
  const [showClips, setShowClips] = useState(false);

  // Background footage: desktop only, and never for users who prefer reduced motion.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 761px) and (prefers-reduced-motion: no-preference)');
    const update = () => setShowClips(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    if (!showClips) return;
    const t = window.setInterval(() => setClip((c) => (c + 1) % HERO_CLIPS.length), 8000);
    return () => window.clearInterval(t);
  }, [showClips]);

  // Arriving from another page (/careers → /#why): the page renders after the browser's
  // own hash jump, so scroll to the section once it exists.
  useEffect(() => {
    const { hash } = window.location;
    if (!hash) return;
    const t = window.setTimeout(() => document.querySelector(hash)?.scrollIntoView({ block: 'start' }), 50);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('.mlp-page [data-reveal]'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('is-in');
            io.unobserve(en.target);
          }
        }),
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));

    // Deep links (#kb-source etc.): the browser's hash jump can land on an
    // element before the observer reports it, so also reveal anything that is
    // already within the viewport now, after the jump settles, and on hash change.
    const revealInView = () => {
      const vh = window.innerHeight;
      els.forEach((el) => {
        if (el.classList.contains('is-in')) return;
        const r = el.getBoundingClientRect();
        if (r.bottom > 0 && r.top < vh) {
          el.classList.add('is-in');
          io.unobserve(el);
        }
      });
    };
    // Belt and braces: the observer can stay silent during programmatic or
    // very fast scrolls, so also check on scroll (rAF-throttled).
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        revealInView();
      });
    };
    revealInView();
    const t = window.setTimeout(revealInView, 600);
    window.addEventListener('hashchange', revealInView);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      io.disconnect();
      window.clearTimeout(t);
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('hashchange', revealInView);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div className="mlp-page">
      <SiteHeader />

      <main id="top">
        {/* Hero */}
        <section className="mlp-hero">
          {showClips && (
            <div className="mlp-hero-bg" aria-hidden="true">
              {HERO_CLIPS.map((src, i) => (
                <video
                  key={src}
                  className={`mlp-hero-clip${i === clip ? ' is-active' : ''}`}
                  src={src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload={i === 0 ? 'auto' : 'metadata'}
                />
              ))}
              <div className="mlp-hero-wash" />
            </div>
          )}
          <div className="mlp-wrap mlp-hero-inner">
            <p className="mlp-hero-kicker">For developers and owner-operators</p>
            <h1 className="mlp-hero-title">
              Grow assets under management,{' '}
              <br />
              <em>not headcount.</em>
            </h1>
            <p className="mlp-hero-lead">
              <span>One super intelligent platform to source, build, lease, and run your properties.</span>{' '}
              <span>Built with practicing developers.</span>
            </p>
            <div className="mlp-hero-ctas">
              <a href="#waitlist" className="mlp-btn mlp-btn--primary">
                Join the waitlist
              </a>
              <a href="#start-free" className="mlp-btn mlp-btn--ghost">
                Start free
              </a>
            </div>
          </div>
        </section>

        {/* Logo banner */}
        <section className="mlp-logos" aria-label="Customers">
          <p className="mlp-logos-label">Trusted by developers and operators in California and New York</p>
          <div className="mlp-marquee">
            <div className="mlp-marquee-track" aria-hidden="false">
              <CustomerLogos />
              <CustomerLogos />
              <CustomerLogos />
            </div>
          </div>
        </section>

        {/* Knightsbridge */}
        <section id="knightsbridge" className="mlp-section">
          <div className="mlp-wrap">
            <div className="mlp-section-head" data-reveal>
              <p className="mlp-kicker mlp-kicker--kb">Knightsbridge · Development management</p>
              <h2 className="mlp-h2">
                From the public record <em>to the closing table.</em>
              </h2>
            </div>
            {KNIGHTSBRIDGE.map((s, i) => (
              <StepRow key={s.verb} step={s} index={i} tone="kb" />
            ))}
          </div>
        </section>

        {/* Sawbridge */}
        <section id="sawbridge" className="mlp-section mlp-section--sand">
          <div className="mlp-wrap">
            <div className="mlp-section-head" data-reveal>
              <p className="mlp-kicker mlp-kicker--sb">Sawbridge · Asset management</p>
              <h2 className="mlp-h2">
                Run the building <em>without the management company.</em>
              </h2>
            </div>
            {SAWBRIDGE.map((s, i) => (
              <StepRow key={s.verb} step={s} index={i} tone="sb" />
            ))}
          </div>
        </section>

        {/* Why */}
        <section id="why" className="mlp-section">
          <div className="mlp-wrap">
            <div className="mlp-section-head mlp-section-head--center" data-reveal>
              <p className="mlp-kicker">Why trust it</p>
              <h2 className="mlp-h2">
                AI agents that know real estate <em>the way you do.</em>
              </h2>
            </div>
            <div className="mlp-pillars" data-reveal>
              {PILLARS.map((p) => (
                <div key={p.title} className="mlp-pillar">
                  <h3>{p.title}</h3>
                  <p>{p.copy}</p>
                </div>
              ))}
            </div>
            <div className="mlp-creds" data-reveal>
              <div className="mlp-creds-group">
                <span className="mlp-creds-label">Our team comes from</span>
                <div className="mlp-creds-logos">
                  <img src={columbiaLogo} alt="Columbia University" style={{ height: 24 }} />
                  <span className="mlp-inst-clip">
                    <img src={stanfordLogo} alt="Stanford University" />
                  </span>
                  <img src={logoMicrosoft} alt="Microsoft" style={{ height: 24 }} />
                  <img src={logoMeta} alt="Meta" style={{ height: 32 }} />
                  <img src={logoBarracuda} alt="Barracuda Networks" style={{ height: 20 }} />
                </div>
              </div>
              <span className="mlp-creds-divider" aria-hidden="true" />
              <div className="mlp-creds-group">
                <span className="mlp-creds-label">Backed by</span>
                <div className="mlp-creds-logos">
                  {BACKERS.map((b) => (
                    <a key={b.name} href={b.href} target="_blank" rel="noopener noreferrer">
                      <img src={b.logo} alt={b.name} style={{ height: b.height }} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
            {/* SOC 2 / GDPR are AWS's certifications for the infrastructure Metlas runs on, not a Metlas audit. */}
            <div className="mlp-creds mlp-creds--security" data-reveal>
              <span className="mlp-creds-label">Security</span>
              <span className="mlp-creds-chip">SOC 2 certified infrastructure</span>
              <span className="mlp-creds-chip">GDPR-compliant infrastructure</span>
            </div>
          </div>
        </section>

        {/* Customers */}
        <section id="customers" className="mlp-section mlp-section--sand">
          <div className="mlp-wrap">
            <div className="mlp-section-head" data-reveal>
              <p className="mlp-kicker">Customers</p>
              <h2 className="mlp-h2">
                What changed <em>for the teams running it.</em>
              </h2>
            </div>
            <div data-reveal>
              <QuoteCarousel quotes={QUOTES} />
            </div>
          </div>
        </section>

        {/* Get started: free tools today, or the full platform via the waitlist */}
        <section id="get-started" className="mlp-section mlp-waitlist">
          <div className="mlp-wrap">
            <div className="mlp-section-head mlp-section-head--center" data-reveal>
              <p className="mlp-kicker">Get started</p>
              <h2 className="mlp-h2">
                Start free today. <em>Or get the whole platform.</em>
              </h2>
            </div>
            <div className="mlp-paths" data-reveal>
              <div id="start-free" className="mlp-card mlp-path">
                <p className="mlp-path-label">Start free · No waitlist</p>
                <h3>Try the free tools now.</h3>
                <p className="mlp-path-lead">Sign up and use them today.</p>
                <div className="mlp-tools">
                  {FREE_TOOLS.map((t) => (
                    <a key={t.name} className="mlp-tool" href={t.href}>
                      <span className="mlp-tool-name">
                        {t.name}
                        {t.note && <span className="mlp-tool-note">{t.note}</span>}
                      </span>
                      <span className="mlp-tool-arrow" aria-hidden="true">
                        →
                      </span>
                      <span className="mlp-tool-copy">{t.copy}</span>
                    </a>
                  ))}
                </div>
              </div>
              <div id="waitlist" className="mlp-card mlp-path">
                <p className="mlp-path-label">Full platform</p>
                <h3>Join the waitlist. We onboard in order.</h3>
                <p className="mlp-path-lead">
                  Tell us about your portfolio or your pipeline. When your turn comes, we set the agents up on
                  your own buildings.
                </p>
                <WaitlistForm />
              </div>
            </div>
          </div>
        </section>

        <footer className="mlp-footer">
          <div className="mlp-wrap mlp-footer-inner">
            <div className="mlp-footer-brand">
              <Logo />
              <span>Trustworthy AI for Real Estate</span>
            </div>
            <nav className="mlp-footer-links" aria-label="Footer">
              <a href="#knightsbridge" onClick={(e) => goTo(e, '#knightsbridge')}>
                Development management
              </a>
              <a href="#sawbridge" onClick={(e) => goTo(e, '#sawbridge')}>
                Asset management
              </a>
              <a href={LINKS.careers}>Careers</a>
              <a href={LINKS.contact}>Contact</a>
              <a href={LINKS.privacyTerms}>Privacy &amp; terms</a>
            </nav>
            <span className="mlp-footer-copy">© {new Date().getFullYear()} Metlas Inc</span>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPageV2;
